import * as React from "react";
import * as Moment from 'moment';
import uniqWith from "lodash-es/uniqWith";
import { VitaloJak2LogFileRecord, VitaloJak2LogEventCode, VitaloJak2LogFile } from "../../misc/api/DeviceLinkApi/VitaloJak2";
import { StudyCoughEventType, CoughAnalysisSetEvent, CoughAnalysisSet } from "../../misc/api/apiTypes";
import Modal from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { AuditModalBody } from "../../auditmodal/components/AuditModalBody";
import SuccessWithMessage from "../../misc/components/SuccessWithMessage";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useWebPortalLegacyClient, WebPortalLegacyClientInternal } from "../../misc/api/WebPortalTokenClient";
import { useCoughMarshalApiClient } from "../../misc/api/CoughMarshalApiClient";

interface ReImportDeviceEventsModalProps {
    selectedAnalysisSet: string;
    onClose: () => void;
}

const uploadDeviceEvents = async (legacyClient: WebPortalLegacyClientInternal, analysisSet: CoughAnalysisSet, records: Array<VitaloJak2LogFileRecord>, annotation: string): Promise<number> => {
    const buttonEventCodes = [
        VitaloJak2LogEventCode.button1Pressed,
        VitaloJak2LogEventCode.button2Pressed,
        VitaloJak2LogEventCode.button3Pressed,
        VitaloJak2LogEventCode.button4Pressed,
        VitaloJak2LogEventCode.muteOn,
        VitaloJak2LogEventCode.muteOff,
    ];

    const events = records.filter(x => buttonEventCodes.includes(x.eventCode));

    const eventTypes = await legacyClient.getStudyCoughEventTypes({
        Filters: [
            {
                Member: "StudyId",
                Values: [analysisSet.Study.Id]
            }
        ]
    });

    const eventsFiltered = events
        .filter(x => buttonEventCodes.includes(x.eventCode))
        .sort((a, b) => Moment(a.dateTime).diff(Moment(b.dateTime)));

    const recordEvents: CoughAnalysisSetEvent[] = [];

    for (const [index, value] of eventsFiltered.entries()) {
        let eventType: StudyCoughEventType = null;
        let rawEnd = null;
        switch (value.eventCode) {
            case VitaloJak2LogEventCode.button1Pressed:
                eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 1);
                break;
            case VitaloJak2LogEventCode.button2Pressed:
                eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 2);
                break;
            case VitaloJak2LogEventCode.button3Pressed:
                eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 3);
                break;
            case VitaloJak2LogEventCode.button4Pressed:
                eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 4);
                break;
            case VitaloJak2LogEventCode.muteOn:
                eventType = eventTypes.Data.find(x => x.AssociatedDeviceButton === 4);

                let tailOfList = eventsFiltered.slice(index + 1, eventsFiltered.length);
                rawEnd = tailOfList.find(x => x.eventCode == VitaloJak2LogEventCode.muteOff);
                break;
        }

        if (eventType) {
            const recordingStart = records.find(x => x.eventCode == VitaloJak2LogEventCode.recordingStarted).dateTime;

            const analysisSetEvent = {
                AnalysisSetId: analysisSet.Id,
                EventTypeId: eventType.Id,
                RawStart: Moment(value.dateTime).diff(Moment(recordingStart)) / 1000,
                RawEnd: rawEnd ? Moment(rawEnd.dateTime).diff(Moment(recordingStart)) / 1000 : null,
            };

            recordEvents.push(analysisSetEvent);
        }

    }

    //Don't want any duration events that are 0 duration.
    const nonZeroDurationEvents = recordEvents.filter(e => e.RawEnd === null || e.RawEnd !== e.RawStart);

    //There are scenarios where the mute start time and end times are the same in the log file. These need to be removed.
    const noDuplicates = uniqWith(nonZeroDurationEvents, (arrVal, othVal) => {
        return arrVal.EventTypeId === othVal.EventTypeId && arrVal.RawStart === othVal.RawStart && arrVal.RawEnd === othVal.RawEnd;
    });

    //There are no device events, re-importing the device events is not required here.
    if (noDuplicates.length === 0) {
        return Promise.resolve(0);
    }

    return await legacyClient.reImportDeviceEvents(analysisSet.Id, noDuplicates, annotation).then(() => noDuplicates.length);
}

interface ReImportDeviceEventsSuccessProps {
    numEvents: number;
    onClose: () => void;
}

const ReImportDeviceEventsSuccess = ({ numEvents, onClose }: ReImportDeviceEventsSuccessProps) => {

    const message = numEvents > 0 ? `${numEvents} events successfully re-imported.` : "There are no events to import.";

    return (<div>

        <div className='modal-body'>
            <SuccessWithMessage message={message} />
        </div>
        <div className='modal-footer'>
            <button className='btn btn-primary' onClick={onClose}>Close</button>
        </div>
    </div>)
};

export const ReImportDeviceEventsModal = ({ selectedAnalysisSet, onClose }: ReImportDeviceEventsModalProps): JSX.Element => {
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>(null);
    const [analysisSet, setAnalysisSet] = React.useState<CoughAnalysisSet>();
    const client = useCoughMarshalApiClient();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [logFile, setLogFile] = React.useState<VitaloJak2LogFile>();
    const [complete, setComplete] = React.useState<boolean>(false);
    const [numImportedEvents, setNumImportedEvents] = React.useState<number>(0);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        if (!selectedAnalysisSet) {
            setAnalysisSet(undefined);
            setLogFile(undefined);
            return;
        }

        setLoading(true);

        legacyClient.getCoughAnalysisSet(selectedAnalysisSet)
            .then(anSet => {
                setAnalysisSet(anSet);
                return client.getRecordingLogFile(anSet.SessionId).then(log => {
                    setLogFile(log);
                    setLoading(false);
                });
            })
            .catch(err => setError(err.Message));

    }, [selectedAnalysisSet]);

    const resetModal = () => {
        setIsSubmitting(false);
        setError(null);
        setLoading(false);
        setComplete(false);
        setNumImportedEvents(0);
    }

    const resetModalAndClose = () => {
        resetModal();
        onClose();
    }

    const renderModalBody = () => {
        if (!logFile)
            return <LoadingWithMessage message="Loading Import Wizard..." />

        if (complete) {
            return <ReImportDeviceEventsSuccess numEvents={numImportedEvents} onClose={resetModalAndClose} />
        }

        return <AuditModalBody isSubmitting={loading || isSubmitting} onSubmit={async (values) => {
            resetModal();
            setLoading(true);

            try {
                const numEventsImported = await uploadDeviceEvents(legacyClient, analysisSet, logFile.records, values.Annotation);
                setComplete(true);
                setNumImportedEvents(numEventsImported);
            } catch (err) {
                setError(err.Message);
            } finally {
                setIsSubmitting(false);
            }


        }} onClose={resetModalAndClose} />
    }

    return (<Modal show={true} closeModalHandler={resetModalAndClose} header="Confirm Re-Import Of Subject Events">
        <Alert show={!!error} category={AlertCategoryEnum.Danger}>
            {error}
        </Alert>
        {renderModalBody()}
    </Modal>);
};
